import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle, Check, Loader, Upload } from 'lucide-react';
const ProfileSettings = () => {
  const {
    user,
    updateProfile
  } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({
    fullName: user?.fullName || '',
    email: user?.email || '',
    phone: user?.phone || '',
    dateOfBirth: user?.dateOfBirth || '',
    nationality: user?.nationality || 'Singapore',
    preferredLanguage: user?.preferredLanguage || 'EN',
    // Emergency contact
    emergencyName: '',
    emergencyRelation: '',
    emergencyPhone: '',
    // Travel preferences
    seatPreference: 'window',
    mealPreference: 'halal',
    specialAssistance: false,
    assistanceDetails: '',
    // Privacy settings
    shareProfileData: true,
    receiveMarketingEmails: true,
    receiveNotifications: true
  });
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(user?.profilePicture || null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfileImage(file);
      // Create a preview
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setProfileImagePreview(event.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);
    try {
      // In a real app, this would upload the image and update the profile
      await updateProfile({
        fullName: formData.fullName
        // Other fields would be updated here
      });
      setSuccess('Profile updated successfully');
      // Scroll to top to show success message
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    } catch (error) {
      if (error instanceof Error) {
        setError(error.message);
      } else {
        setError('Failed to update profile. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div data-id="element-778">
      <h1 className="text-2xl font-bold text-navy-900 mb-6" data-id="element-779">
        Profile Settings
      </h1>
      {success && <div className="bg-green-50 text-green-700 p-4 rounded-md mb-6 flex items-center" data-id="element-780">
          <Check size={20} className="mr-2" data-id="element-781" />
          <span data-id="element-782">{success}</span>
        </div>}
      {error && <div className="bg-red-50 text-red-700 p-4 rounded-md mb-6 flex items-center" data-id="element-783">
          <AlertCircle size={20} className="mr-2" data-id="element-784" />
          <span data-id="element-785">{error}</span>
        </div>}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-8" data-id="element-786">
        <div className="flex border-b border-gray-200" data-id="element-787">
          <button onClick={() => setActiveTab('personal')} className={`px-6 py-4 font-medium text-sm ${activeTab === 'personal' ? 'border-b-2 border-navy-900 text-navy-900' : 'text-navy-600 hover:text-navy-800'}`} data-id="element-788">
            Personal Information
          </button>
          <button onClick={() => setActiveTab('emergency')} className={`px-6 py-4 font-medium text-sm ${activeTab === 'emergency' ? 'border-b-2 border-navy-900 text-navy-900' : 'text-navy-600 hover:text-navy-800'}`} data-id="element-789">
            Emergency Contact
          </button>
          <button onClick={() => setActiveTab('preferences')} className={`px-6 py-4 font-medium text-sm ${activeTab === 'preferences' ? 'border-b-2 border-navy-900 text-navy-900' : 'text-navy-600 hover:text-navy-800'}`} data-id="element-790">
            Travel Preferences
          </button>
          <button onClick={() => setActiveTab('privacy')} className={`px-6 py-4 font-medium text-sm ${activeTab === 'privacy' ? 'border-b-2 border-navy-900 text-navy-900' : 'text-navy-600 hover:text-navy-800'}`} data-id="element-791">
            Privacy Settings
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-792">
          {activeTab === 'personal' && <div className="p-6" data-id="element-793">
              <div className="mb-6 flex flex-col items-center" data-id="element-794">
                <div className="w-32 h-32 rounded-full overflow-hidden bg-gray-200 mb-4" data-id="element-795">
                  {profileImagePreview ? <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" data-id="element-796" /> : <div className="w-full h-full flex items-center justify-center text-gray-400" data-id="element-797">
                      <User size={64} data-id="element-798" />
                    </div>}
                </div>
                <label className="cursor-pointer bg-navy-50 hover:bg-navy-100 text-navy-900 px-4 py-2 rounded-md flex items-center transition-colors" data-id="element-799">
                  <Upload size={16} className="mr-2" data-id="element-800" />
                  <span data-id="element-801">Upload Photo</span>
                  <input type="file" accept="image/*" onChange={handleImageChange} className="hidden" data-id="element-802" />
                </label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-803">
                <div data-id="element-804">
                  <label htmlFor="fullName" className="block text-navy-900 font-medium mb-2" data-id="element-805">
                    Full Name
                  </label>
                  <input type="text" id="fullName" name="fullName" value={formData.fullName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-806" />
                </div>
                <div data-id="element-807">
                  <label htmlFor="email" className="block text-navy-900 font-medium mb-2" data-id="element-808">
                    Email Address
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" disabled // Email is typically not changeable without verification
              data-id="element-809" />
                </div>
                <div data-id="element-810">
                  <label htmlFor="phone" className="block text-navy-900 font-medium mb-2" data-id="element-811">
                    Phone Number
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-812" />
                </div>
                <div data-id="element-813">
                  <label htmlFor="dateOfBirth" className="block text-navy-900 font-medium mb-2" data-id="element-814">
                    Date of Birth
                  </label>
                  <input type="date" id="dateOfBirth" name="dateOfBirth" value={formData.dateOfBirth} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-815" />
                </div>
                <div data-id="element-816">
                  <label htmlFor="nationality" className="block text-navy-900 font-medium mb-2" data-id="element-817">
                    Nationality
                  </label>
                  <select id="nationality" name="nationality" value={formData.nationality} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-818">
                    <option value="Singapore" data-id="element-819">Singapore</option>
                    <option value="Malaysia" data-id="element-820">Malaysia</option>
                    <option value="Indonesia" data-id="element-821">Indonesia</option>
                    <option value="Brunei" data-id="element-822">Brunei</option>
                    <option value="Other" data-id="element-823">Other</option>
                  </select>
                </div>
                <div data-id="element-824">
                  <label className="block text-navy-900 font-medium mb-2" data-id="element-825">
                    Preferred Language
                  </label>
                  <div className="flex space-x-4 mt-2" data-id="element-826">
                    <label className="flex items-center" data-id="element-827">
                      <input type="radio" name="preferredLanguage" value="EN" checked={formData.preferredLanguage === 'EN'} onChange={handleChange} className="mr-2" data-id="element-828" />
                      <span data-id="element-829">English</span>
                    </label>
                    <label className="flex items-center" data-id="element-830">
                      <input type="radio" name="preferredLanguage" value="MY" checked={formData.preferredLanguage === 'MY'} onChange={handleChange} className="mr-2" data-id="element-831" />
                      <span data-id="element-832">Malay</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>}
          {activeTab === 'emergency' && <div className="p-6" data-id="element-833">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-834">
                <div data-id="element-835">
                  <label htmlFor="emergencyName" className="block text-navy-900 font-medium mb-2" data-id="element-836">
                    Emergency Contact Name
                  </label>
                  <input type="text" id="emergencyName" name="emergencyName" value={formData.emergencyName} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="Full name of emergency contact" data-id="element-837" />
                </div>
                <div data-id="element-838">
                  <label htmlFor="emergencyRelation" className="block text-navy-900 font-medium mb-2" data-id="element-839">
                    Relationship
                  </label>
                  <input type="text" id="emergencyRelation" name="emergencyRelation" value={formData.emergencyRelation} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="e.g. Spouse, Parent, Sibling" data-id="element-840" />
                </div>
                <div data-id="element-841">
                  <label htmlFor="emergencyPhone" className="block text-navy-900 font-medium mb-2" data-id="element-842">
                    Emergency Contact Phone
                  </label>
                  <input type="tel" id="emergencyPhone" name="emergencyPhone" value={formData.emergencyPhone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" placeholder="Include country code if international" data-id="element-843" />
                </div>
              </div>
            </div>}
          {activeTab === 'preferences' && <div className="p-6" data-id="element-844">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-845">
                <div data-id="element-846">
                  <label htmlFor="seatPreference" className="block text-navy-900 font-medium mb-2" data-id="element-847">
                    Seat Preference
                  </label>
                  <select id="seatPreference" name="seatPreference" value={formData.seatPreference} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-848">
                    <option value="window" data-id="element-849">Window Seat</option>
                    <option value="aisle" data-id="element-850">Aisle Seat</option>
                    <option value="middle" data-id="element-851">Middle Seat</option>
                    <option value="no-preference" data-id="element-852">No Preference</option>
                  </select>
                </div>
                <div data-id="element-853">
                  <label htmlFor="mealPreference" className="block text-navy-900 font-medium mb-2" data-id="element-854">
                    Meal Preference
                  </label>
                  <select id="mealPreference" name="mealPreference" value={formData.mealPreference} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500" data-id="element-855">
                    <option value="halal" data-id="element-856">Halal Meal</option>
                    <option value="vegetarian" data-id="element-857">Vegetarian</option>
                    <option value="seafood" data-id="element-858">Seafood</option>
                    <option value="diabetic" data-id="element-859">Diabetic Meal</option>
                    <option value="no-preference" data-id="element-860">No Preference</option>
                  </select>
                </div>
                <div className="md:col-span-2" data-id="element-861">
                  <label className="flex items-start" data-id="element-862">
                    <input type="checkbox" name="specialAssistance" checked={formData.specialAssistance} onChange={handleChange} className="mt-1 mr-2" data-id="element-863" />
                    <span className="text-navy-900" data-id="element-864">
                      I require special assistance during travel
                    </span>
                  </label>
                </div>
                {formData.specialAssistance && <div className="md:col-span-2" data-id="element-865">
                    <label htmlFor="assistanceDetails" className="block text-navy-900 font-medium mb-2" data-id="element-866">
                      Special Assistance Details
                    </label>
                    <textarea id="assistanceDetails" name="assistanceDetails" value={formData.assistanceDetails} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gold-500 h-32" placeholder="Please provide details about the special assistance you require" data-id="element-867"></textarea>
                  </div>}
              </div>
            </div>}
          {activeTab === 'privacy' && <div className="p-6" data-id="element-868">
              <div className="space-y-4" data-id="element-869">
                <div data-id="element-870">
                  <label className="flex items-start" data-id="element-871">
                    <input type="checkbox" name="shareProfileData" checked={formData.shareProfileData} onChange={handleChange} className="mt-1 mr-2" data-id="element-872" />
                    <div data-id="element-873">
                      <span className="text-navy-900 font-medium" data-id="element-874">
                        Share my profile data with travel partners
                      </span>
                      <p className="text-navy-600 text-sm mt-1" data-id="element-875">
                        This allows us to share your information with hotels,
                        airlines, and other travel partners to provide better
                        service.
                      </p>
                    </div>
                  </label>
                </div>
                <div data-id="element-876">
                  <label className="flex items-start" data-id="element-877">
                    <input type="checkbox" name="receiveMarketingEmails" checked={formData.receiveMarketingEmails} onChange={handleChange} className="mt-1 mr-2" data-id="element-878" />
                    <div data-id="element-879">
                      <span className="text-navy-900 font-medium" data-id="element-880">
                        Receive marketing emails
                      </span>
                      <p className="text-navy-600 text-sm mt-1" data-id="element-881">
                        Stay updated with our latest offers, promotions, and
                        travel packages.
                      </p>
                    </div>
                  </label>
                </div>
                <div data-id="element-882">
                  <label className="flex items-start" data-id="element-883">
                    <input type="checkbox" name="receiveNotifications" checked={formData.receiveNotifications} onChange={handleChange} className="mt-1 mr-2" data-id="element-884" />
                    <div data-id="element-885">
                      <span className="text-navy-900 font-medium" data-id="element-886">
                        Receive notifications
                      </span>
                      <p className="text-navy-600 text-sm mt-1" data-id="element-887">
                        Get notified about booking updates, travel alerts, and
                        account activities.
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            </div>}
          <div className="p-6 bg-gray-50 border-t border-gray-200 flex justify-end" data-id="element-888">
            <button type="submit" disabled={isSubmitting} className="bg-navy-900 hover:bg-navy-800 text-white px-6 py-3 rounded-md font-medium transition-colors flex items-center" data-id="element-889">
              {isSubmitting ? <>
                  <Loader size={20} className="animate-spin mr-2" data-id="element-890" />
                  Saving...
                </> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default ProfileSettings;